import React, { useState, useEffect, useCallback } from "react";
import {
  message,
  Modal,
  Spin,
} from "antd";
import {
  PlusOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import {
  fetchBots as apiFetchBots,
  fetchStats as apiFetchStats,
  createBot,
  startBot,
  stopBot,
  getLogs as apiGetLogs,
  getChats as apiGetChats,
  sendBotCommand,
  fetchActions,
  executeBotAction,
  deleteBot,
} from "../../services/botApiV3";
import { unwrap } from "../../utils/apiHelpers";
import { useBotModals } from "../../hooks/useBotModals";
import { useBotFilters } from "../../hooks/useBotFilters";
import BotFilterBar from "../../components/control/BotFilterBar";
import BotStatsCards from "../../components/control/BotStatsCards";
import BotTable from "../../components/control/BotTable";
import CreateBotModal from "../../components/control/CreateBotModal";
import ManageBotModal from "../../components/control/ManageBotModal";
import ControlBotModal from "../../components/control/ControlBotModal";
import ConfigBotModal from "../../components/control/ConfigBotModal";
import LogsModal from "../../components/control/LogsModal";
import ChatsModal from "../../components/control/ChatsModal";
import "../../styles/botcontrol.css";

const BotControl = () => {
  const {
    isCreateVisible,
    isManageVisible,
    isControlVisible,
    isLogsVisible,
    isChatsVisible,
    selectedBot,
    openCreate,
    openManage,
    openControl,
    openLogs,
    openChats,
    closeCreate,
    closeManage,
    closeControl,
    closeLogs,
    closeChats,
  } = useBotModals();

  const [isMobile, setIsMobile] = useState(false);
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ onlineCount: 0, totalCount: 0 });
  const [logs, setLogs] = useState([]);
  const [chats, setChats] = useState([]);
  const [lineCount, setLineCount] = useState(50);
  const [actions, setActions] = useState([]);
  const [isConfigVisible, setIsConfigVisible] = useState(false);

  const { filterStatus, setFilterStatus, filterServer, setFilterServer, searchText, setSearchText, serverList, filteredBots } = useBotFilters(bots);

  // 检测屏幕宽度
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // 加载数据
  const loadBots = useCallback(async () => {
    try {
      setLoading(true);
      const data = await unwrap(apiFetchBots());
      setBots(data.bots);
    } catch (error) {
      console.error("获取假人列表失败:", error);
      message.error("获取假人列表失败: " + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const data = await apiFetchStats();
      setStats(data);
    } catch (error) {
      console.error("获取统计数据失败:", error);
    }
  }, []);

  // 初始化 + 定时刷新
  useEffect(() => {
    loadBots();
    loadStats();
    const intervalId = setInterval(() => {
      loadBots();
      loadStats();
    }, 10000);
    return () => clearInterval(intervalId);
  }, [loadBots, loadStats]);

  // 创建假人
  const handleCreate = async (values) => {
    try {
      setLoading(true);
      const data = await unwrap(createBot(values));
      message.success(data.message || "假人创建成功");
      closeCreate();
      loadBots();
      loadStats();
    } catch (error) {
      console.error("创建假人失败:", error);
      message.error("创建假人失败: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 启动/停止假人
  const handleStartStop = async (bot) => {
    try {
      setLoading(true);
      const isRunning = bot.status === "在线" || bot.status === "启动中";

      if (isRunning) {
        // 停止 - 直接执行
        const data = await unwrap(stopBot(bot.id));
        message.success(data.message || "假人停止成功");
        loadBots();
        loadStats();
        closeManage();
      } else {
        // 启动 - 先普通启动
        try {
          const data = await unwrap(startBot(bot.id));
          message.success(data.message || "假人启动成功");
          loadBots();
          loadStats();
          closeManage();
        } catch (startError) {
          // 启动失败 → 询问是否强制启动
          Modal.confirm({
            className: "botcontrol-confirm-modal",            icon: <ExclamationCircleOutlined />,            title: "启动失败",
            content: "启动失败，是否强制启动假人？",
            okText: "强制启动",
            cancelText: "取消",
                        onOk: async () => {
              try {
                const forceData = await unwrap(startBot(bot.id, { force: true }));
                message.success(forceData.message || "假人强制启动成功");
                loadBots();
                loadStats();
                closeManage();
              } catch (forceError) {
                console.error("强制启动失败:", forceError);
                message.error("强制启动失败: " + (forceError?.message || "未知错误"));
              }
            },
          });
        }
      }
    } catch (error) {
      console.error("操作失败:", error);
      message.error("操作失败: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 打开日志
  const handleOpenLogs = async () => {
    if (!selectedBot) return;
    try {
      setLoading(true);
      const data = await unwrap(apiGetLogs(selectedBot.id, { lines: lineCount }));
      setLogs(data.logs);
      openLogs();
    } catch (error) {
      console.error("获取日志失败:", error);
      message.error("获取日志失败: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 日志行数变化时重新获取
  const handleLogLineCountChange = async (count) => {
    setLineCount(count);
    if (!selectedBot) return;
    try {
      const data = await unwrap(apiGetLogs(selectedBot.id, { lines: count }));
      setLogs(data.logs);
    } catch (error) {
      console.error("获取日志失败:", error);
      message.error("获取日志失败: " + error.message);
    }
  };

  // 打开聊天记录
  const handleOpenChats = async () => {
    if (!selectedBot) return;
    try {
      setLoading(true);
      const data = await unwrap(apiGetChats(selectedBot.id, { lines: lineCount }));
      setChats(data.chats);
      openChats();
    } catch (error) {
      console.error("获取聊天记录失败:", error);
      message.error("获取聊天记录失败: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 聊天行数变化时重新获取
  const handleChatLineCountChange = async (count) => {
    setLineCount(count);
    if (!selectedBot) return;
    try {
      const data = await unwrap(apiGetChats(selectedBot.id, { lines: count }));
      setChats(data.chats);
    } catch (error) {
      console.error("获取聊天记录失败:", error);
      message.error("获取聊天记录失败: " + error.message);
    }
  };

  // 打开配置弹窗
  const handleOpenConfig = () => {
    setIsConfigVisible(true);
  };

  // 配置保存成功回调
  const handleConfigSaved = useCallback(() => {
    loadBots();
    loadStats();
  }, [loadBots, loadStats]);

  // 删除假人
  const handleDeleteBot = useCallback(async (bot) => {
    Modal.confirm({
      className: "botcontrol-confirm-modal",      icon: <DeleteOutlined />,      title: "确认删除",
      content: `确定要删除假人 "${bot.name}" 吗？此操作不可撤销。`,
      okText: "删除",
      okType: "danger",
      cancelText: "取消",
      onOk: async () => {
        try {
          const data = await unwrap(deleteBot(bot.id));
          message.success(data.message || "假人已删除");
          loadBots();
          loadStats();
          closeManage();
        } catch (error) {
          console.error("删除假人失败:", error);
          message.error("删除假人失败: " + error.message);
        }
      },
    });
  }, [loadBots, loadStats, closeManage]);

  // 发送命令
  const handleSendCommand = async (command) => {
    if (!selectedBot) return;
    try {
      const data = await unwrap(sendBotCommand(selectedBot.id, command));
      message.success(data.message || "命令发送成功");
    } catch (error) {
      console.error("发送命令失败:", error);
      message.error("发送命令失败: " + error.message);
    }
  };

  // 打开控制面板 — 先获取动作列表
  const handleOpenControl = async () => {
    if (!selectedBot) return;
    try {
      const data = await unwrap(fetchActions());
      setActions(data);
    } catch (error) {
      console.error("获取动作列表失败:", error);
      message.error("获取动作列表失败: " + error.message);
    }
    openControl();
  };

  // 执行动作（v3：actionName + params）
  const handleExecuteAction = async (actionName, params = {}) => {
    if (!selectedBot) return;
    try {
      const data = await unwrap(executeBotAction(selectedBot.id, actionName, params));
      message.success(data.message || "动作执行成功");
    } catch (error) {
      console.error("执行动作失败:", error);
      message.error("执行动作失败: " + error.message);
    }
  };

  return (
    <>
      <button
        className="botcontrol-create-btn"
        onClick={openCreate}
        disabled={loading}
      >
        {loading ? <LoadingOutlined /> : <PlusOutlined />}
      </button>
      <motion.div
        className="botcontrol-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <BotFilterBar
          filterServer={filterServer}
          filterStatus={filterStatus}
          searchText={searchText}
          serverList={serverList}
          loading={loading}
          onServerChange={setFilterServer}
          onStatusChange={setFilterStatus}
          onSearchChange={setSearchText}
        />

        <BotStatsCards
          onlineCount={stats.onlineCount}
          totalCount={stats.totalCount}
        />

        <Spin spinning={loading} tip="加载中...">
          <BotTable
            bots={filteredBots}
            loading={false}
            isMobile={isMobile}
            onManage={openManage}
          />
        </Spin>

        <CreateBotModal
          visible={isCreateVisible}
          loading={loading}
          bots={bots}
          onCreate={handleCreate}
          onCancel={closeCreate}
        />

        <ManageBotModal
          visible={isManageVisible}
          bot={selectedBot}
          loading={loading}
          onStartStop={handleStartStop}
          onOpenControl={handleOpenControl}
          onOpenLogs={handleOpenLogs}
          onOpenChats={handleOpenChats}
          onDelete={handleDeleteBot}
          onOpenConfig={handleOpenConfig}
          onCancel={closeManage}
        />

        <ControlBotModal
          visible={isControlVisible}
          bot={selectedBot}
          loading={loading}
          actions={actions}
          onSendCommand={handleSendCommand}
          onExecuteAction={handleExecuteAction}
          onCancel={closeControl}
        />

        <ConfigBotModal
          visible={isConfigVisible}
          bot={selectedBot}
          loading={loading}
          onCancel={() => setIsConfigVisible(false)}
          onSuccess={handleConfigSaved}
        />

        <LogsModal
          visible={isLogsVisible}
          bot={selectedBot}
          logs={logs}
          loading={loading}
          lineCount={lineCount}
          onLineCountChange={handleLogLineCountChange}
          onCancel={closeLogs}
        />

        <ChatsModal
          visible={isChatsVisible}
          bot={selectedBot}
          chats={chats}
          loading={loading}
          lineCount={lineCount}
          onLineCountChange={handleChatLineCountChange}
          onCancel={closeChats}
        />
      </motion.div>
    </>
  );
};

export default BotControl;
